import React from "react";
import { ShieldCheck } from "lucide-react";
import logo from "../../assets/logo.svg";

export const formatCertificateDate = (value) => {
  if (!value) return "—";

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `${day}/${month}/${year}`;
};

const CertificateDocument = ({
  studentName,
  courseTitle,
  grade,
  completionDate,
  certificateId,
  instructorName,
  issueDate,
  isVerified = true,
  className = "",
}) => {
  return (
    <div
      className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-[#FED7AA] bg-gradient-to-b from-[#FFF7ED] to-[#FAF5FF] px-[26px] pb-16 pt-[26px] text-center shadow-[0_1px_2px_0_#0000000D] ${className}`}
    >
      <div className="w-full max-w-[672px]">
        <img
          src={logo}
          alt="Steam Minds"
          className="mx-auto mt-12 h-[72px] w-auto object-contain"
        />

        <h3 className="mt-4 text-3xl font-bold text-[#111827]">
          Certificate of Completion
        </h3>
        <p className="mt-9 text-lg text-[#374151]">
          This is to certify that
        </p>

        <div className="mx-auto mt-6 inline-block w-full rounded-lg border-2 border-[#FED7AA] bg-white px-4 py-4">
          <p className="text-2xl font-bold text-[#111827]">
            {studentName || "—"}
          </p>
        </div>

        <p className="mt-6 text-lg text-[#374151]">
          has successfully completed the course
        </p>

        <div className="mx-auto mt-6 inline-block w-full rounded-lg border-2 border-[#E9D5FF] bg-white px-4 py-4">
          <p className="text-xl font-bold text-[#111827]">
            {courseTitle || "—"}
          </p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-sm text-[#4B5563]">Completion Date</p>
            <p className="text-base font-semibold text-[#111827]">
              {formatCertificateDate(completionDate)}
            </p>
          </div>
          <div>
            <p className="text-sm text-[#4B5563]">Grade Achieved</p>
            <p className="text-base font-semibold text-[#111827]">
              {grade || "—"}
            </p>
          </div>
          {instructorName ? (
            <div>
              <p className="text-sm text-[#4B5563]">Instructor</p>
              <p className="text-base font-semibold text-[#111827]">
                {instructorName}
              </p>
            </div>
          ) : null}
          {issueDate ? (
            <div>
              <p className="text-sm text-[#4B5563]">Issue Date</p>
              <p className="text-base font-semibold text-[#111827]">
                {formatCertificateDate(issueDate)}
              </p>
            </div>
          ) : null}
          <div
            className={`rounded-lg border border-dashed border-[#D1D5DB] bg-white/70 px-3 py-3 ${
              instructorName && issueDate ? "sm:col-span-2 lg:col-span-4" : ""
            }`}
          >
            <p className="text-sm text-[#4B5563]">Certificate ID</p>
            {certificateId ? (
              <p className="mt-1 uppercase break-all text-base font-semibold text-[#111827]">
                {certificateId}
              </p>
            ) : (
              <>
                <p className="mt-1 text-sm font-medium text-[#6B7280]">
                  Not assigned yet
                </p>
                <p className="mt-1 text-xs text-[#9CA3AF]">
                  Generate your certificate to receive a unique ID.
                </p>
              </>
            )}
          </div>
        </div>

        <div className="mt-10 flex flex-col items-stretch justify-between gap-8 border-t border-[#E5E7EB] pt-10 sm:flex-row">
          <div className="flex-1 text-center sm:text-left">
            <div className="mx-auto max-w-[220px] border-b-2 border-[#923D8C] pb-2 sm:mx-0">
              <p
                className="text-3xl text-[#923D8C]"
                style={{ fontFamily: "cursive" }}
              >
                Steam Minds
              </p>
            </div>
            <p className="mt-2 text-sm font-medium text-[#4B5563]">
              Digital Signature
            </p>
            <p className="mt-1 text-xs text-[#9CA3AF]">
              Authorized by Steam Minds Learning Platform
            </p>
          </div>

          <div className="flex flex-1 text-center sm:text-right">
            <div className="mx-auto w-full max-w-[220px] sm:ml-auto sm:mr-0">
              <div
                className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full border sm:ml-auto sm:mr-0 ${
                  isVerified
                    ? "border-[#D1FAE5] bg-[#F0FDF4] text-[#059669]"
                    : "border-[#FED7AA] bg-white/80 text-[#9333EA]"
                }`}
              >
                {isVerified ? (
                  <ShieldCheck size={18} strokeWidth={2} />
                ) : (
                  <span className="text-[9px] font-semibold uppercase tracking-wide">
                    Preview
                  </span>
                )}
              </div>
              <p className="mt-3 text-sm font-medium text-[#4B5563]">
                {isVerified ? "Verified Certificate" : "Certificate Preview"}
              </p>
              <p className="mt-1 text-xs text-[#9CA3AF]">
                {isVerified
                  ? "Officially issued by Steam Minds"
                  : "Generate to finalize your certificate"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CertificateDocument;
